/**
 * ConversationHeader Component
 *
 * Header bar for the active conversation showing name, participants,
 * and action buttons (mute, bitacora link, salir, eliminar).
 *
 * #509: tenía además props `onSearch` y `onArchive`. Medido: el único caller
 * del componente es `ChatView` (`src/components/chat/ChatView.js:301`) y no
 * pasaba ninguna de las dos. `onArchive` no se renderizaba en ningún lado —
 * estaba sólo declarada — y el ítem "Buscar" vivía detrás de `{onSearch && …}`,
 * o sea que tampoco llegaba nunca a la pantalla.
 *
 * El issue las describía como afordancias muertas ("se ven, se clickean, no
 * hacen nada"); no lo eran: eran props muertas, invisibles para el usuario.
 * Se borran en vez de cablearse porque cablearlas sería alcance nuevo
 * (buscar dentro de una conversación / archivar) y no deuda.
 *
 * @module components/chat/ConversationHeader
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Dropdown, Modal } from 'react-bootstrap';
import KebabMenu from '../common/KebabMenu';

const getConversationDisplayName = (conversation) => {
  if (!conversation) return '';
  if (conversation.type === 'direct' && conversation.dmParticipant) {
    return conversation.dmParticipant.full_name || 'Usuario';
  }
  if (conversation.name) return conversation.name;
  if (conversation.project?.title) return `# ${conversation.project.title}`;
  return 'Conversación';
};

const getConversationIcon = (conversation) => {
  if (!conversation) return 'bi-chat-left-text';
  switch (conversation.type) {
    case 'channel': return 'bi-hash';
    case 'direct': return 'bi-person-fill';
    case 'group': return 'bi-people-fill';
    default: return 'bi-chat-left-text';
  }
};

const getSubtitle = (conversation, participants) => {
  if (!conversation) return '';

  if (conversation.type === 'direct') {
    const other = conversation.dmParticipant;
    if (other?.role) {
      const roleLabels = { admin: 'Admin', supervisor: 'Supervisor', technician: 'Tecnico', operario: 'Operario', mensajero: 'Mensajero', client: 'Cliente' };
      return roleLabels[other.role] || other.role;
    }
    return 'Mensaje directo';
  }

  const count = participants?.length || conversation.participantCount || 0;
  if (count === 0) return '';

  if (count <= 3 && participants?.length > 0) {
    return participants.map(p => p.user?.full_name?.split(' ')[0] || '').filter(Boolean).join(', ');
  }

  return `${count} participante${count !== 1 ? 's' : ''}`;
};

const ConversationHeader = ({
  conversation,
  participants = [],
  onBack,
  onMute,
  onLinkBitacora,
  onDelete,
  onLeave,
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  if (!conversation) return null;

  const isMuted = conversation.isMuted;
  const isDM = conversation.type === 'direct';
  const isChannel = conversation.type === 'channel';
  const isGroup = conversation.type === 'group';

  const deleteLabel = isDM ? 'Eliminar chat' : isChannel ? 'Eliminar canal' : 'Eliminar grupo';

  return (
    <div className="chat-header">
      {/* Back button (mobile) */}
      {onBack && (
        <button
          className="chat-header-back"
          onClick={onBack}
          aria-label="Volver"
        >
          <i className="bi bi-arrow-left" style={{ fontSize: '1.2rem' }} />
        </button>
      )}

      {/* Avatar */}
      <div className="chat-header-avatar">
        <i className={`bi ${getConversationIcon(conversation)}`} style={{ fontSize: '1.1rem' }} />
      </div>

      {/* Info */}
      <div className="chat-header-info">
        <h6 className="chat-header-title">
          {getConversationDisplayName(conversation)}
        </h6>
        <p className="chat-header-subtitle">
          {getSubtitle(conversation, participants)}
        </p>
      </div>

      {/* Action buttons */}
      <div className="d-flex align-items-center gap-1">
        {onLinkBitacora && (isChannel || conversation.project_id) && (
          <Button
            variant="link"
            className="text-white p-1"
            onClick={onLinkBitacora}
            title="Vincular a bitácora"
          >
            <i className="bi bi-journal-bookmark" />
          </Button>
        )}

        {/* Kebab menu */}
        <KebabMenu
          label={`Acciones de la conversación con ${getConversationDisplayName(conversation)}`}
          toggleClassName="kebab-toggle text-white p-1"
          stopPropagation={false}
        >
          {onMute && (
            <Dropdown.Item onClick={onMute}>
              <i className={`bi ${isMuted ? 'bi-bell' : 'bi-bell-slash'} me-2`} />
              {isMuted ? 'Activar notificaciones' : 'Silenciar'}
            </Dropdown.Item>
          )}
          {(isGroup || isChannel) && onLeave && (
            <>
              <Dropdown.Divider />
              <Dropdown.Item onClick={onLeave} className="text-warning">
                <i className="bi bi-box-arrow-left me-2" />
                Salir {isChannel ? 'del canal' : 'del grupo'}
              </Dropdown.Item>
            </>
          )}
          {onDelete && (
            <>
              <Dropdown.Divider />
              <Dropdown.Item onClick={() => setShowDeleteConfirm(true)} className="text-danger">
                <i className="bi bi-trash me-2" />
                {deleteLabel}
              </Dropdown.Item>
            </>
          )}
        </KebabMenu>
      </div>

      {/* Delete confirmation modal */}
      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)} centered size="sm">
        <Modal.Header closeButton>
          <Modal.Title as="h6">{deleteLabel}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0">
            {isDM
              ? 'Se eliminara este chat. Los mensajes se conservaran.'
              : `Se eliminara ${isChannel ? 'este canal' : 'este grupo'} para todos los participantes.`}
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" size="sm" onClick={() => setShowDeleteConfirm(false)}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            size="sm"
            onClick={() => {
              setShowDeleteConfirm(false);
              onDelete();
            }}
          >
            Eliminar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

ConversationHeader.propTypes = {
  conversation: PropTypes.shape({
    id: PropTypes.string,
    type: PropTypes.string,
    name: PropTypes.string,
    project_id: PropTypes.string,
    project: PropTypes.object,
    dmParticipant: PropTypes.object,
    participantCount: PropTypes.number,
    isMuted: PropTypes.bool,
  }),
  participants: PropTypes.array,
  onBack: PropTypes.func,
  onMute: PropTypes.func,
  onLinkBitacora: PropTypes.func,
  onDelete: PropTypes.func,
  onLeave: PropTypes.func,
};

export default ConversationHeader;
